import React, { useState, useEffect } from 'react';
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
import { getStockConversions, fetchStockConversion, destroyStockConversion } from '../../../api/StockConversionsAPI';
import { handleApiError } from '../../../api/risapi';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { format, parseISO } from 'date-fns';

const StockConversionsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [entityName, setEntityName] = useState('Stock Conversion');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);
    
    const [conversionList, setConversionList] = useState({
        paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null,
    });

    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [saveCompleted, setSaveCompleted] = useState(false);

    // --- Navigation ---
    const handleAddClick = () => navigate('/franchisee_user/inventory/stock-conversions/create');
    const handleEditClick = (id) => navigate(`/franchisee_user/inventory/stock-conversions/edit/${id}`);

    // --- Delete Logic ---
    const handleDeleteClick = (id) => {
        fetchStockConversion(id, function (result) {
            if (result.stock_conversion) {
                const deleteItem = result.stock_conversion;
                Swal.fire({
                    title: 'Are you sure?',
                    html: `
                        <p>Reverse (Delete) Stock Conversion:</p>
                        <ul class="delete-details-list">
                            <li><strong>ID:</strong> ${deleteItem.id}</li>
                            <li><strong>Product:</strong> ${deleteItem.product?.name || 'N/A'}</li>
                            <li><strong>Action:</strong> ${deleteItem.quantity_converted} ${deleteItem.from_uom?.short_name} ➔ ${deleteItem.base_quantity_generated} ${deleteItem.to_uom?.short_name}</li>
                        </ul>
                        <p class="text-danger">This will attempt to reverse the stock movement. It will fail if the loosened stock has already been used.</p>`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#6c757d',
                    confirmButtonText: 'Yes, delete it!'
                }).then((dialogResult) => {
                    if (dialogResult.isConfirmed) {
                        destroyStockConversion(deleteItem, function (apiResponse) {
                            if (apiResponse.stock_conversion) {
                                Swal.fire('Deleted!', 'The conversion has been reversed.', 'success');
                                setSaveCompleted(prev => !prev);
                            } else {
                                Swal.fire('Error!', apiResponse.error?.response?.data?.message || 'Could not delete conversion.', 'error');
                            }
                        });
                    }
                });
            } else { Swal.fire('Error!', 'Could not fetch details for deletion.', 'error'); }
        });
    };

    // --- Effects ---
    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success', message: successMessage });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        document.title = 'Manage Stock Loosening';
        let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };
        setViewDataLoading(true);

        getStockConversions(filters, function (response) {
            setViewDataLoading(false);
            if (response.stock_conversions) {
                 let data = []; let currentP = 1; let lastP = 1; let responsePP = perPage;
                 if (Array.isArray(response.stock_conversions)) { data = response.stock_conversions; }
                 else if (response.stock_conversions.data && Array.isArray(response.stock_conversions.data)) { data = response.stock_conversions.data; currentP = response.stock_conversions.current_page; lastP = response.stock_conversions.last_page; responsePP = response.stock_conversions.per_page; }
                 else { console.error("API structure error:", response.stock_conversions); handleApiError(response); return; }
                 setConversionList({ paginate: filters.paginate, per_page: responsePP, data: data, current_page: currentP, tot_page: lastP });
            } else {
                 handleApiError(response); setToaster({ type: 'danger', heading: 'Error', message: 'Could not load stock conversions.' }); setShowToaster(true);
                 setConversionList(prev => ({ ...prev, data: [], current_page: 1, tot_page: 1 }));
            }
        });
    }, [currentPage, searchTermCompleted, saveCompleted, perPage]);

    // Helper to format date
    const formatDate = (dateString) => {
         if (!dateString) return 'N/A';
         try { return format(parseISO(dateString), 'dd-MMM-yyyy'); } catch (error) { return dateString; }
     };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title='Stock Loosening'>
                        <li><a className="f-s-14 f-w-500" href="#">Inventory</a></li>
                        <li className="active"><a className="f-s-14 f-w-500" href="#">Stock Loosening</a></li>
                    </AppBreadCrumb>

                    <div className="row bg-white">
                        <div className="col-12">
                            <div className="card">
                                 <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
                                     <h5 className="mb-0">Stock Conversion History</h5>
                                     <div className='d-flex align-items-center mt-2 mt-md-0'>
                                        <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                        <button className="btn btn-red btn-sm ms-2" type="button" onClick={handleAddClick}>
                                            <i className="ph-bold  ph-circles-three-plus"></i> New Product Loosening
                                        </button>
                                     </div>
                                 </div>
                                <div className="card-body p-0">
                                    <div className="d-flex flex-wrap m-3 border-bottom pb-3 align-items-center">
                                        {conversionList.data.length > 0 ? (
                                            <>
                                                <AppPaginator entityName={entityName} currentPage={conversionList.current_page} totalPages={conversionList.tot_page} setCurrentPage={setCurrentPage} />
                                                <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                            </>
                                        ) : ( !viewDataLoading && <p className="text-muted w-100 mb-0">No stock conversions found.</p> )}
                                        {(viewDataLoading) && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-hover table-striped mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Product</th>
                                                    <th className='text-end'>Qty. Converted</th>
                                                    <th>From Unit</th>
                                                    <th className='text-center'>➔</th>
                                                    <th className='text-end'>Qty. Generated</th>
                                                    <th>Base Unit</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {!viewDataLoading && conversionList.data.map((item) => (
                                                    <tr key={item.id}>
                                                        <td>{formatDate(item.conversion_date)}</td>
                                                        <td>{item.product?.name || 'N/A'}</td>
                                                        <td className='text-end'>{parseFloat(item.quantity_converted || 0).toFixed(2)}</td>
                                                        <td>{item.from_uom?.short_name || 'N/A'}</td>
                                                        <td className='text-center'>➔</td>
                                                        <td className='text-end'>{parseFloat(item.base_quantity_generated || 0).toFixed(2)}</td>
                                                        <td>{item.to_uom?.short_name || 'N/A'}</td>
                                                        <td>
                                                            <button className="btn btn-sm btn-outline-success me-1 px-1 py-0" title="Edit" onClick={() => handleEditClick(item.id)}> <i className="ti ti-edit f-s-14"></i> </button>
                                                            <button className="btn btn-sm btn-outline-danger px-1 py-0" title="Delete" onClick={() => handleDeleteClick(item.id)}> <i className="ti ti-trash f-s-14"></i> </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                {viewDataLoading && ( <tr><td colSpan="8" className="text-center">Loading...</td></tr> )}
                                                {!viewDataLoading && conversionList.data.length === 0 && ( <tr><td colSpan="8" className="text-center">No conversions found</td></tr> )}
                                            </tbody>
                                        </table>
                                    </div>
                                    
                                    
                                </div> {/* card-body */}
                            </div> {/* card */}
                        </div> {/* col */}
                    </div> {/* row */}
                </div> {/* container-fluid */}
            </main>
        </>
    );
};

export default StockConversionsPage;